import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { Grid, IconButton, Paper, Stack } from '@mui/material'
import FormInput from 'components/FormInput'
import { ItemOption } from 'components/FormInputSelect'
import React, { useCallback, useEffect } from 'react'
import { UseFieldArrayRemove, useFormContext } from 'react-hook-form'
import { isDocumentFormatValid } from 'utils/document.util'
import { DocumentTypeId } from 'utils/enums/document-type.enum'
import { documentMask } from 'utils/mask.util'
import { FormInputFile } from 'components/FormInputFile'
import { SocioeducationMeasureIdentificationType } from 'types/socioeducation-measure-identification.type'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'

type DocumentItemProps = {
  index: number
  typeOptions: ItemOption[]
  onRemove: UseFieldArrayRemove
  isRemovable: boolean
  disabled?: boolean
}

export const DocumentItem: React.FC<DocumentItemProps> = ({
  index,
  typeOptions,
  onRemove,
  isRemovable,
  disabled,
}) => {
  const { control, watch, clearErrors, setValue, setError } =
    useFormContext<SocioeducationMeasureIdentificationType>()

  const documentTypeIdIdValue = watch(`pessoaFisica.documentos.${index}.tipoDocumento.id`)
  const documentNumberValue = watch(`pessoaFisica.documentos.${index}.numeroDocumento`)

  const isBirthOrDeathCertificate =
    String(documentTypeIdIdValue) === DocumentTypeId.CERTIDAO_DE_NASCIMENTO ||
    String(documentTypeIdIdValue) === DocumentTypeId.CERTIDAO_DE_OBITO

  const validateDocumentNumber = useCallback((): void => {
    const hasTypeAndNumber = documentTypeIdIdValue && documentNumberValue
    if (
      hasTypeAndNumber &&
      isDocumentFormatValid(String(documentTypeIdIdValue), documentNumberValue)
    ) {
      clearErrors(`pessoaFisica.documentos.${index}.numeroDocumento`)
    }
  }, [isBirthOrDeathCertificate, documentTypeIdIdValue, documentNumberValue, clearErrors, index])

  const disableAsDocumentTypeNotEntered = (): boolean => {
    if (disabled) return true
    return !documentTypeIdIdValue
  }

  useEffect(() => {
    if (!isBirthOrDeathCertificate) {
      setValue(`pessoaFisica.documentos.${index}.livro`, '')
      setValue(`pessoaFisica.documentos.${index}.folha`, '')
      setValue(`pessoaFisica.documentos.${index}.termo`, '')
    }
    setValue(
      `pessoaFisica.documentos.${index}.numeroDocumento`,
      documentMask(String(documentTypeIdIdValue), documentNumberValue),
    )
  }, [documentTypeIdIdValue, documentNumberValue, setValue, index])

  useEffect(() => {
    validateDocumentNumber()
  }, [validateDocumentNumber])

  return (
    <Paper variant='outlined' sx={{ p: 3, width: '100%', backgroundColor: 'white' }}>
      <Grid container spacing={2} rowSpacing={4}>
        <Grid item md={4} xs={12}>
          <FormInputAutoComplete
            control={control}
            name={`pessoaFisica.documentos.${index}.tipoDocumento.id`}
            label='Tipo de documento'
            options={typeOptions}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            getOptionById={(id: string | null, options: ItemOption[]) =>
              options.find((option) => Number(option.value) === Number(id)) || null
            }
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            onChangeValue={(option) => Number(option?.value) || null}
          />
        </Grid>
        <Grid item md={8} xs={12}>
          <Stack spacing={1} direction='row'>
            {isBirthOrDeathCertificate && (
              <FormInput
                control={control}
                name={`pessoaFisica.documentos.${index}.matricula`}
                label={'Matrícula'}
                maxLength={40}
                placeholder='Informe a matrícula'
                disabled={disableAsDocumentTypeNotEntered()}
              />
            )}
            {!isBirthOrDeathCertificate && (
              <FormInput
                control={control}
                name={`pessoaFisica.documentos.${index}.numeroDocumento`}
                label={'Documento'}
                maxLength={40}
                placeholder='Informe o documento'
                disabled={disableAsDocumentTypeNotEntered()}
              />
            )}
            <FormInputFile
              control={control}
              name={`pessoaFisica.documentos.${index}.arquivoBase64`}
              nameFilename={`pessoaFisica.documentos.${index}.arquivoNome`}
              nameFiletype={`pessoaFisica.documentos.${index}.arquivoTipo`}
              setValue={setValue}
              setError={setError}
              clearErrors={clearErrors}
              disabled={disableAsDocumentTypeNotEntered()}
            />
            {isRemovable && (
              <IconButton
                onClick={() => onRemove(index)}
                sx={{ maxHeight: 40 }}
                disabled={disabled}
              >
                <DeleteOutlineRoundedIcon color={disabled ? 'disabled' : 'error'} />
              </IconButton>
            )}
          </Stack>
        </Grid>
        {isBirthOrDeathCertificate && (
          <>
            <Grid item md={4} xs={12}>
              <FormInput
                control={control}
                name={`pessoaFisica.documentos.${index}.livro`}
                label='Livro'
                maxLength={5}
                placeholder='Informe o Livro'
                disabled={disableAsDocumentTypeNotEntered()}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <FormInput
                control={control}
                name={`pessoaFisica.documentos.${index}.folha`}
                label='Folha'
                maxLength={5}
                placeholder='Informe a Folha'
                disabled={disableAsDocumentTypeNotEntered()}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <FormInput
                control={control}
                name={`pessoaFisica.documentos.${index}.termo`}
                label='Termo'
                maxLength={10}
                placeholder='Informe o Termo'
                disabled={disableAsDocumentTypeNotEntered()}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  )
}
